/* eslint-disable @typescript-eslint/no-use-before-define */
import { FilterRowCount } from '@src/components/common/filterDropdownStyles';
import { MatchNav } from '@src/components/common/MatchNav';
import { COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import TopicBadge from '@src/components/common/TopicBadge';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import CheckIconAlt from '@src/svgs/CheckIconAlt';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import React, { useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

export type TopicFilterMode = 'highlight' | 'show-only';

interface TopicFilterDropdownProps {
  topics: string[];
  selectedTopics: string[];
  onApply: (topics: string[]) => void;
  /**
   * Optional Highlight ↔ Show Only segmented control. When both `mode` and
   * `onModeChange` are provided the toggle renders above the topic list;
   * otherwise the dropdown only exposes topic selection (existing behavior).
   */
  mode?: TopicFilterMode;
  onModeChange?: (mode: TopicFilterMode) => void;
  /**
   * How many of the caller's items each topic covers, shown at the row's right.
   * A topic counted 0 is grouped at the foot of the list and cannot be chosen:
   * picking it could only empty the list, which reads as the filter being broken
   * rather than as the topic being unused.
   *
   * Optional — callers that do not count leave every topic selectable, which is
   * the behaviour this dropdown had everywhere before.
   */
  clipCounts?: ReadonlyMap<string, number>;
  /**
   * Matched paragraph count per topic title, rendered after each row's badge
   * (PLAT-11039) — tells the user how much transcript a topic covers before
   * they select it. Topics missing from the map render no count.
   *
   * Unlike `clipCounts` an uncounted topic stays selectable and ungrouped: on
   * the transcript every topic is a legitimate filter. Pass at most one of the
   * two — they are the same column rendered under different rules.
   */
  matchCounts?: ReadonlyMap<string, number>;
  /**
   * Why this filter is currently unusable, or null when it is usable
   * (PLAT-11182). Set while the Highlights filter is in Show Only: the two
   * show-only sets would intersect, so a topic could silently resolve to an
   * empty transcript. Renders the trigger inert with this text as its tooltip,
   * mirroring how the Quotes/Clips pills block on the same collision.
   */
  disabledReason?: string | null;
  /**
   * Prev/next navigation over the paragraphs the selected topics matched,
   * fused to the right of the trigger — the Quotes/Clips pill idiom applied to
   * this filter, so a topic selection can be stepped through instead of only
   * looked at. Omitted, or `total: 0`, renders no nav.
   */
  matchNav?: {
    /** 1-based index of the active match. */
    current: number;
    total: number;
    onPrev: () => void;
    onNext: () => void;
  };
}

const TopicFilterDropdown: React.FC<TopicFilterDropdownProps> = ({
  topics,
  selectedTopics,
  onApply,
  mode,
  onModeChange,
  clipCounts,
  matchCounts,
  disabledReason = null,
  matchNav,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Anchor on the container (trigger + fused nav) so the panel's right edge
  // lines up with the visible pill, not just the trigger half; clicks on the
  // fused nav are treated as inside the anchor and don't dismiss the menu.
  const anchorRef = useRef<HTMLDivElement>(null);
  // Per-instance tooltip id so multiple dropdowns on the same page don't
  // cross-wire tooltips. react-tooltip resolves by id, so colliding ids
  // surface the wrong content on hover.
  const modeTooltipId = useId();
  const triggerTooltipId = useId();
  // Never inert the filter that currently owns Show Only: each dropdown blocks
  // the other, so blocking both would leave no way to turn either off.
  const isDisabled = !!disabledReason && mode !== 'show-only';

  // Close on the way into the inert state — otherwise a menu opened before the
  // Highlights filter switched to Show Only would stay up and stay clickable.
  if (isDisabled && isOpen) setIsOpen(false);

  // Portal + anchor the panel below the trigger (right-aligned); the position is
  // computed at open, so selecting a topic — which widens the trigger with the
  // count — doesn't shift the open menu. The hook owns dismissal (PLAT).
  const { menuRef, style } = useAnchoredDropdown(
    isOpen,
    anchorRef,
    () => setIsOpen(false),
    480,
    { align: 'end', width: 280 },
  );

  const handleToggleTopic = (topic: string) => {
    const next = selectedTopics.includes(topic)
      ? selectedTopics.filter(t => t !== topic)
      : [...selectedTopics, topic];
    onApply(next);
  };

  // The list order is kept; the empty ones are only moved to the end, so a topic
  // a creator remembers by position does not also change its rank.
  const withClips = clipCounts
    ? topics.filter(topic => (clipCounts.get(topic) ?? 0) > 0)
    : topics;
  const withoutClips = clipCounts
    ? topics.filter(topic => (clipCounts.get(topic) ?? 0) === 0)
    : [];

  // Mirrors the Highlights bulk action (PLAT-11182). Simpler here: topics have
  // no hidden flag and no "empty means all" shorthand, so the two states are
  // just every topic and none — scoped to the selectable ones, since a topic no
  // clip was cut from is not a choice the bulk action can make either.
  const allSelected =
    withClips.length > 0 &&
    withClips.every(topic => selectedTopics.includes(topic));

  const renderTopic = (topic: string) => {
    const isSelected = selectedTopics.includes(topic);
    // Only one of the two ever renders; both use the shared row-count pill so
    // this menu counts the same way as the Highlights menu beside it
    // (PLAT-11182).
    const clipCount = clipCounts?.get(topic);
    const matchCount = matchCounts?.get(topic);
    return (
      <TopicItem key={topic} onClick={() => handleToggleTopic(topic)}>
        <CheckboxIndicator selected={isSelected}>
          {isSelected && <CheckIconAlt />}
        </CheckboxIndicator>
        <TopicBadge>{topic}</TopicBadge>
        {clipCount !== undefined && (
          <FilterRowCount>{clipCount}</FilterRowCount>
        )}
        {matchCount != null && matchCount > 0 && (
          <FilterRowCount>{matchCount}</FilterRowCount>
        )}
      </TopicItem>
    );
  };

  // MatchNav self-hides at `total <= 0`, so the fused styling keys off the same
  // condition rather than off `matchNav` being passed.
  const showMatchNav = !isDisabled && !!matchNav && matchNav.total > 0;

  return (
    <Container ref={anchorRef} $fused={showMatchNav}>
      {/* `aria-disabled`, not the native `disabled` attribute — a natively
          disabled button fires no pointer events, so the tooltip explaining
          WHY it is inert would never show (same reason as the toggle pills). */}
      <TriggerButton
        $disabled={isDisabled}
        aria-disabled={isDisabled}
        onClick={isDisabled ? undefined : () => setIsOpen(prev => !prev)}
        data-tooltip-id={triggerTooltipId}
        data-tooltip-content={disabledReason ?? undefined}
      >
        Story Topics
        {/* Selected-topic count as plain muted "(n)", matching the Highlights
            trigger (PLAT-11039). Per-topic match counts live on the rows. */}
        {selectedTopics.length > 0 && (
          <TopicCount>({selectedTopics.length})</TopicCount>
        )}
        <Caret $open={isOpen}>
          <ChevronDownIcon
            width="12"
            height="12"
            strokeColor={isDisabled ? 'var(--text-muted)' : 'var(--accent)'}
          />
        </Caret>
      </TriggerButton>
      {/* Neutral tint, not a topic color: several topics can be selected at
          once and the rails already carry their colors, so the strip stays the
          design-system default rather than picking one of them. */}
      {showMatchNav && matchNav && (
        <MatchNav
          attached
          current={matchNav.current}
          total={matchNav.total}
          onPrev={matchNav.onPrev}
          onNext={matchNav.onNext}
          prevLabel="Previous topic match"
          nextLabel="Next topic match"
        />
      )}
      {isDisabled && (
        <Tooltip
          id={triggerTooltipId}
          style={COMPACT_TOOLTIP_STYLE}
          place="top"
          positionStrategy="fixed"
        />
      )}

      {isOpen &&
        style &&
        createPortal(
          <Dropdown ref={menuRef} style={style}>
            {mode && onModeChange && (
              <>
                <ModeToggle role="group" aria-label="Topic filter mode">
                  <ModeButton
                    type="button"
                    $active={mode === 'highlight'}
                    onClick={() => onModeChange('highlight')}
                    aria-pressed={mode === 'highlight'}
                    data-tooltip-id={modeTooltipId}
                    data-tooltip-content="Mark matched paragraphs in place — full transcript stays visible"
                  >
                    {/* "Show All", not "Highlight" (PLAT-11039/11182): this
                        sits next to the Highlights feature (saved segments), so
                        "Highlight" read as the same thing — and naming the pair
                        for how much transcript stays visible explains itself. */}
                    Show All
                  </ModeButton>
                  <ModeButton
                    type="button"
                    $active={mode === 'show-only'}
                    onClick={() => onModeChange('show-only')}
                    aria-pressed={mode === 'show-only'}
                    data-tooltip-id={modeTooltipId}
                    data-tooltip-content="Hide everything except matched paragraphs"
                  >
                    Show Only
                  </ModeButton>
                </ModeToggle>
                <Tooltip
                  id={modeTooltipId}
                  style={COMPACT_TOOLTIP_STYLE}
                  place="top"
                  positionStrategy="fixed"
                />
              </>
            )}
            {/* Same bulk action as the Highlights menu (PLAT-11182), labeled
                with the state it moves to. Clear sits beside it for the partial
                selection the toggle cannot undo — undoing one checkbox at a
                time is the tedious part of a multi-select. */}
            <ListActions>
              {selectedTopics.length > 0 && !allSelected && (
                <ClearButton
                  type="button"
                  data-testid="topic-filter-clear"
                  onClick={() => onApply([])}
                >
                  Clear
                </ClearButton>
              )}
              <SelectAllButton
                type="button"
                onClick={() => onApply(allSelected ? [] : [...withClips])}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </SelectAllButton>
            </ListActions>
            <TopicList>
              {withClips.map(renderTopic)}
              {withoutClips.length > 0 && (
                <>
                  {/* Kept on the list rather than dropped: a creator looking for
                      a topic needs to find it and see that nothing was cut from
                      it, which is different from it not existing. */}
                  <GroupHeader data-testid="topic-filter-empty-group">
                    Topics Without Clips
                  </GroupHeader>
                  {withoutClips.map(topic => (
                    <TopicItem
                      key={topic}
                      aria-disabled
                      $disabled
                      data-testid={`topic-filter-disabled-${topic}`}
                    >
                      <CheckboxIndicator selected={false} />
                      <TopicBadge>{topic}</TopicBadge>
                      <FilterRowCount>None</FilterRowCount>
                    </TopicItem>
                  ))}
                </>
              )}
            </TopicList>
          </Dropdown>,
          document.body,
        )}
    </Container>
  );
};

export default TopicFilterDropdown;

const Container = styled.div<{ $fused?: boolean }>`
  display: inline-flex;
  align-items: stretch;
  height: 100%;

  /* Fused with the match nav when one is mounted: the trigger's right corners
     square off and its right padding tightens, so trigger + counter read as
     one pill — the same join the Quotes/Clips pills use. */
  ${p =>
    p.$fused &&
    `> button:first-child {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      padding-right: 3px;
    }`}
`;

/* PLAT-11039: borderless language-selector trigger recipe (TranslationToggle's
   \`.Selected\` override) — gray like the Quotes label, hover matches the
   header's PanelButton idiom. Selection is signaled by the count, not a
   border. */
const TriggerButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  height: 100%;
  border: none;
  border-radius: var(--radius-button);
  background: transparent;
  color: ${({ $disabled }) =>
    $disabled ? 'var(--text-muted)' : 'var(--text-secondary)'};
  /* Same size as the language trigger and the Quotes/Clips labels
     (PLAT-11039). */
  font-size: var(--text-xs);
  font-weight: 500;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  white-space: nowrap;
  transition: all 150ms ease-out;

  /* Inert: muted, no hover response — the tooltip carries the reason
     (PLAT-11182), matching the Quotes/Clips pills' blocked state. */
  &:hover {
    background-color: ${({ $disabled }) =>
      $disabled ? 'transparent' : 'var(--surface-elevated)'};
    color: ${({ $disabled }) =>
      $disabled ? 'var(--text-muted)' : 'var(--foreground)'};
  }
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  transition: transform var(--transition-fast);
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

const Dropdown = styled.div`
  /* Darker --background surface (matching the language overflow menu) so the
     tabs, badges and rows read with contrast; compact padding/gap per the DS
     menu density. */
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: var(--spacing-sm);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  box-shadow: var(--elevation-toast);
`;

/* Bulk-action row above the list (PLAT-11182), right-aligned per the
   Select All / Deselect All convention. Borderless rather than
   SpeakerFilterComponent's bordered pill: inside a 280px menu that already
   carries the mode toggle, a bordered button competed with it. Kept in sync
   with the twin in SavedSegmentsFilterDropdown. */
const ListActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--spacing-xs);
`;

const SelectAllButton = styled.button`
  border: none;
  background: transparent;
  padding: 2px 6px;
  border-radius: var(--radius-button);
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 150ms ease-out;

  &:hover {
    background-color: var(--surface-elevated);
    color: var(--foreground);
  }
`;

/** A text action, not a button: it undoes a selection rather than committing. */
const ClearButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: var(--text-muted);
  font-family: inherit;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }
`;

const TopicList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 240px;
  overflow-y: auto;
`;

const TopicItem = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px;
  cursor: ${props => (props.$disabled ? 'default' : 'pointer')};
  border-radius: var(--radius-button);
  transition: background-color 0.1s ease;
  /* Grayed whole — badge included — so "not available" is one signal rather than
     a readable row with a dead checkbox. */
  opacity: ${props => (props.$disabled ? 0.45 : 1)};

  &:hover {
    background-color: ${props =>
      props.$disabled ? 'transparent' : 'var(--surface-elevated)'};
  }
`;

/* Muted count for the trigger's selected-topic total — muted so the label stays
   the focal point (PLAT-11039). Row counts use the shared `FilterRowCount`
   pill instead (PLAT-11182). */
const TopicCount = styled.span`
  font-size: 11px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

/** Sub-heading inside the list, quieter than the dropdown's own header. */
const GroupHeader = styled.div`
  margin-top: var(--spacing-xs);
  padding: 4px 8px 2px;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-muted);
`;

const CheckboxIndicator = styled.div<{ selected: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid var(--border);
  background-color: ${props =>
    props.selected ? 'var(--accent)' : 'transparent'};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ModeToggle = styled.div`
  display: inline-flex;
  align-self: flex-start;
  padding: 2px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background-color: var(--surface-elevated, transparent);
`;

const ModeButton = styled.button<{ $active: boolean }>`
  border: none;
  background-color: ${props =>
    props.$active ? 'var(--accent)' : 'transparent'};
  color: ${props =>
    props.$active ? 'var(--accent-foreground)' : 'var(--text-secondary)'};
  font-size: var(--text-xs);
  font-weight: ${props => (props.$active ? 600 : 500)};
  padding: 3px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    color: ${props =>
      props.$active ? 'var(--accent-foreground)' : 'var(--foreground)'};
  }
`;
